// The browser already knows its own coordinates for free, once the user
// grants the standard geolocation permission prompt -- this is only ever
// requested when a message is actually being sent (never on page load
// unconditionally), and the result is remembered for a few minutes so it
// rides along on every subsequent chat request without re-prompting or
// re-fetching each time. Mirrors browserTimeZone() in conversationsApi.ts:
// fail-soft everywhere, so a denied/unavailable/slow location never blocks
// or delays sending a message.

const MAX_AGE_MS = 5 * 60 * 1000;

let cachedCoords: { latitude: number; longitude: number; at: number } | null = null;

function currentPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      () => resolve(null), // denied, unavailable, or timed out -- fail soft either way
      { maximumAge: MAX_AGE_MS, timeout: 5000 },
    );
  });
}

// Deliberately has no sticky "permission denied, stop asking" latch: a
// browser never re-prompts once denied for an origin, so retrying costs
// nothing but one fast, silent, immediately-failing call -- and it's what
// lets a user who denies the prompt, then later grants it from the
// browser's own site-settings UI (no page reload), actually get their
// location on their very next message instead of being stuck until they
// reload the page.
export async function browserLocation(): Promise<{ latitude: number; longitude: number } | null> {
  if (cachedCoords && Date.now() - cachedCoords.at < MAX_AGE_MS) return cachedCoords;
  const position = await currentPosition();
  if (!position) return null;
  cachedCoords = { latitude: position.coords.latitude, longitude: position.coords.longitude, at: Date.now() };
  return cachedCoords;
}
